import * as React from 'react'
import { cn } from '../../lib/utils'

interface WorkspaceShellProps {
  children: React.ReactNode
  className?: string
}

/**
 * WorkspaceShell - Main layout container for the workspace.
 */
export function WorkspaceShell({ children, className }: WorkspaceShellProps) {
  return (
    <div
      className={cn(
        'h-screen w-screen flex overflow-hidden bg-background',
        className
      )}
    >
      {children}
    </div>
  )
}

interface WorkspaceSidebarProps {
  children: React.ReactNode
  className?: string
  collapsed?: boolean
  width?: number
  mobileMode?: boolean
}

/**
 * WorkspaceSidebar - Resizable sidebar for the director chat panel.
 */
export function WorkspaceSidebar({
  children,
  className,
  collapsed = false,
  width = 360,
  mobileMode = false,
}: WorkspaceSidebarProps) {
  const ref = React.useRef<HTMLElement>(null)

  // After the sidebar collapse/expand CSS transition finishes, dispatch a
  // window resize event so ReactFlow recalculates its internal container
  // dimensions.  Without this, fitView() called right after collapsing can
  // use stale dimensions and push nodes off-screen.
  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const handler = (e: TransitionEvent) => {
      if (e.propertyName === 'width') {
        window.dispatchEvent(new Event('resize'))
      }
    }
    el.addEventListener('transitionend', handler)
    return () => el.removeEventListener('transitionend', handler)
  }, [])

  return (
    <aside
      ref={ref}
      // Marks the Director chatbox subtree so canvas auto-focus suppression
      // ignores interactions here ("action in the Director chatbox area doesn't
      // count" — RFC: suppress auto-focus during canvas interaction).
      data-canvas-interaction-ignore=""
      className={cn(
        'bg-card border-r border-border flex flex-col relative transition-all duration-150',
        mobileMode ? 'h-full w-full border-r-0' : 'flex-shrink-0',
        !mobileMode && collapsed ? 'w-14' : '',
        className
      )}
      style={collapsed || mobileMode ? undefined : { width }}
    >
      {children}
    </aside>
  )
}

interface WorkspaceMainProps {
  children: React.ReactNode
  className?: string
}

/**
 * WorkspaceMain - Main content area (canvas container).
 */
export function WorkspaceMain({ children, className }: WorkspaceMainProps) {
  return (
    <main className={cn('flex-1 relative overflow-hidden bg-canvas', className)}>
      {children}
    </main>
  )
}

interface WorkspaceHeaderProps {
  children: React.ReactNode
  className?: string
}

/**
 * WorkspaceHeader - Top header bar for workspace controls.
 */
export function WorkspaceHeader({ children, className }: WorkspaceHeaderProps) {
  return (
    <header
      className={cn(
        'flex items-center justify-between px-4 h-11 border-b border-border bg-card',
        className
      )}
    >
      {children}
    </header>
  )
}

interface SidebarHeaderProps {
  children: React.ReactNode
  className?: string
}

/**
 * SidebarHeader - Header section of the sidebar.
 */
export function SidebarHeader({ children, className }: SidebarHeaderProps) {
  return (
    <div className={cn('p-3 border-b border-border', className)}>
      {children}
    </div>
  )
}

interface SidebarContentProps {
  children: React.ReactNode
  className?: string
}

/**
 * SidebarContent - Scrollable content area of the sidebar.
 */
export function SidebarContent({ children, className }: SidebarContentProps) {
  return (
    <div className={cn('flex-1 overflow-hidden', className)}>
      {children}
    </div>
  )
}

interface ResizeHandleProps {
  // Pointer events (not mouse events) so the drag can use setPointerCapture and
  // reliably receive pointerup/pointercancel even when the pointer is released
  // outside the window. See ChatSidebar resize handling.
  onPointerDown: (e: React.PointerEvent) => void
  className?: string
  testId?: string
}

/**
 * ResizeHandle - Draggable handle for resizing panels.
 */
export function ResizeHandle({ onPointerDown, className, testId }: ResizeHandleProps) {
  return (
    <div
      data-testid={testId}
      onPointerDown={onPointerDown}
      className={cn(
        'absolute right-0 top-0 bottom-0 w-1 cursor-col-resize z-10',
        // Divider between the Director panel and the canvas — chrome, so it
        // takes the neutral token rather than flashing amber on hover.
        'hover:bg-chrome/20 transition-colors',
        className
      )}
    />
  )
}

export { WorkspaceShell as default }
